import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import type { SxProps } from "@mui/material/styles";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import type { TopUpItem } from "@polifonia/interlude/models";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { EMPTY_STRING_PLACEHOLDER } from "@/appConstant";
import { NumberFormat } from "@/components/NumberFormat";
import { Typography } from "@/components/Typography";
import {
  getPaymentMethodLabels,
  TOPUP_TABLE_COLUMN_COUNT,
} from "@/pages/billing/v2/tabs/usageDetails/utils";

const styles = {
  cell: {
    paddingTop: 0,
    paddingBottom: 0,
    borderBottom: "none",
  },
  content: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "16px 32px",
    padding: "16px 24px",
    backgroundColor: theme.colors.neutral010,
    borderRadius: "4px",
    marginBottom: "8px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  label: {
    color: theme.colors.neutral050,
    fontSize: "0.75rem",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface TopupExpandedRowProps {
  item: TopUpItem;
  open: boolean;
}

export const TopupExpandedRow: React.FC<TopupExpandedRowProps> =
  function TopupExpandedRow({ item, open }) {
    const { t } = useTranslation();
    const paymentMethodLabels = getPaymentMethodLabels(t);

    return (
      <TableRow>
        <TableCell sx={styles.cell} colSpan={TOPUP_TABLE_COLUMN_COUNT}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={styles.content}>
              {/* Row 1 */}
              <Box sx={styles.field}>
                <Box component="span" sx={styles.label}>
                  {t("billing.topup.detail.tax")}
                </Box>
                <Typography variant="body">
                  <NumberFormat
                    numberFormatPreset="currency"
                    numberFormatOptions={{ currency: item.currency }}
                    value={item.taxAmount}
                  />
                </Typography>
              </Box>
              <Box sx={styles.field}>
                <Box component="span" sx={styles.label}>
                  {t("billing.topup.detail.paymentMethod")}
                </Box>
                <Typography variant="body">
                  {item.paymentMethod
                    ? paymentMethodLabels[item.paymentMethod]
                    : EMPTY_STRING_PLACEHOLDER}
                </Typography>
              </Box>
              <Box sx={styles.field}>
                <Box component="span" sx={styles.label}>
                  {t("billing.topup.detail.total")}
                </Box>
                <Typography variant="body">
                  <NumberFormat
                    numberFormatPreset="currency"
                    numberFormatOptions={{ currency: item.currency }}
                    value={item.totalAmount}
                  />
                </Typography>
              </Box>
              {/* Row 2 */}
              <Box sx={styles.field}>
                <Box component="span" sx={styles.label}>
                  {t("billing.topup.detail.companyName")}
                </Box>
                <Typography variant="body">{item.companyName}</Typography>
              </Box>
              <Box sx={styles.field}>
                <Box component="span" sx={styles.label}>
                  {t("billing.topup.detail.taxId")}
                </Box>
                <Typography variant="body">
                  {item.taxId ?? EMPTY_STRING_PLACEHOLDER}
                </Typography>
              </Box>
              <Box sx={styles.field}>
                <Box component="span" sx={styles.label}>
                  {t("billing.topup.detail.memo")}
                </Box>
                <Typography variant="body">
                  {item.memo ?? EMPTY_STRING_PLACEHOLDER}
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };
